import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { EntryPath } from '@/Config/app/router/entry.path';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { PasswordInput } from '@/Domain/components/Shared/Input/PasswordInput';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import LockLineIcon from '@/Domain/components/icons/LockLineIcon';
import { useResetPassword } from '@/Domain/hooks/Auth/useResetPassword.hook';

export const ResetPasswordPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation(['reset-password', 'reset_password_success']);
  const { track } = useAnalytics();

  const {
    onVerify,
    isPending,
    isSuccess,
    onSubmit,
    handleSubmit,
    errors,
    register,
    useV2,
    control,
  } = useResetPassword();

  const handleSuccess = () => {
    track('rest_password_back_success_click', {
      event_category: 'reset_password_form',
      event_label: 'User reset password',
    });

    navigate(EntryPath.login, { viewTransition: true });
  };

  const handleBackTrack = () =>
    track('rest_password_back', {
      event_category: 'reset_password_form',
      event_label: 'User back to login',
    });

  if (isSuccess) {
    return (
      <div className="animate-in fade-in duration-300">
        {/* Success State */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="font-semibold text-2xl text-gray-950 mb-2">
            {t('reset_password_success:title')}
          </h1>
        </div>
        <p className="text-gray-600 text-center mb-6">{t('reset_password_success:description')}</p>
        <Button variant="dark" size="lg" className="w-full" onClick={handleSuccess}>
          {t('reset_password_success:link')}
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-semibold text-2xl text-gray-950 mb-2">{t('reset-password:title')}</h1>
        <p className="text-gray-500 text-sm">{t('reset-password:subtitle')}</p>
      </div>

      <div className="grid gap-5">
        {/* Token Error */}
        {errors.token && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 animate-in fade-in slide-in-from-top-2 duration-200">
            <p className="text-sm text-red-700 font-medium">
              {t('reset-password:validation.invalid_token')}
            </p>
          </div>
        )}

        {/* Form */}
        <form
          id="reset-password-form"
          className="flex flex-col gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <GoogleReCaptcha onVerify={onVerify} />

          {/* New Password Field */}
          <div className="space-y-1.5">
            <label htmlFor="password1" className="text-sm font-medium text-gray-700">
              {t('reset-password:form.password')}
            </label>
            <PasswordInput
              id="password1"
              size="lg"
              placeholder={t('reset-password:form.password_placeholder')}
              {...register('new_password1')}
              error={!!errors.new_password1}
              autoFocus
              autoComplete="new-password"
              startIcon={<LockLineIcon className="h-4 w-4 text-gray-400" />}
            />
            {errors.new_password1 && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.new_password1.message}
              </p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-1.5">
            <label htmlFor="password2" className="text-sm font-medium text-gray-700">
              {t('reset-password:form.confirm_password')}
            </label>
            <PasswordInput
              id="password2"
              size="lg"
              placeholder={t('reset-password:form.password_placeholder')}
              {...register('new_password2')}
              error={!!errors.new_password2}
              autoComplete="new-password"
              startIcon={<LockLineIcon className="h-4 w-4 text-gray-400" />}
            />
            {errors.new_password2 && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.new_password2.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            variant="dark"
            size="lg"
            type="submit"
            disabled={isPending}
            className="w-full mt-1"
          >
            {isPending ? <Spinner /> : t('reset-password:button.submit')}
          </Button>

          {/* reCAPTCHA v2 Fallback */}
          {useV2 && (
            <Controller
              name="recaptcha_response_v2"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange }, fieldState: { invalid } }) => (
                <ReCaptchaCheckbox onChange={onChange} isInvalid={invalid} />
              )}
            />
          )}
        </form>

        {/* Back to Login Link */}
        <div className="text-center pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            {t('reset-password:back_to')}{' '}
            <Link
              className="text-gray-700 hover:text-black font-medium transition-colors hover-underline"
              to="/"
              onClick={handleBackTrack}
            >
              {t('reset-password:link.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
